"use client";

import Link from "next/link";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type {
  TopClientItem,
  RelanceItem,
  ProspectItem,
} from "@/hooks/use-dashboard-data";

// ── Top Clients (horizontal bar chart) ──
export function TopClientsChart({ data }: { data: TopClientItem[] }) {
  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
            Meilleurs Clients
          </h3>
          <p className="py-8 text-center text-xs text-muted-foreground">
            Aucune donnée
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
          Meilleurs Clients
        </h3>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 0, right: 10 }}
          >
            <XAxis
              type="number"
              tick={{
                fill: "hsl(var(--muted-foreground))",
                fontSize: 10,
              }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) =>
                v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
              }
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{
                fill: "hsl(var(--foreground))",
                fontSize: 11,
              }}
              axisLine={false}
              tickLine={false}
              width={110}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 8,
                color: "hsl(var(--foreground))",
              }}
              formatter={(v) => [
                `${Number(v ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`,
                "CA",
              ]}
            />
            <Bar
              dataKey="ca"
              fill="#10B981"
              radius={[0, 4, 4, 0]}
              barSize={14}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ── Clients à relancer ──
export function ClientsRelanceList({ data }: { data: RelanceItem[] }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-muted-foreground">
            Clients à relancer
          </h3>
          {data.length > 0 && (
            <span className="text-xs font-mono text-muted-foreground">
              {data.length}
            </span>
          )}
        </div>
        <div className="max-h-[180px] space-y-1.5 overflow-y-auto">
          {data.length === 0 ? (
            <p className="py-8 text-center text-xs text-muted-foreground">
              Aucune relance
            </p>
          ) : (
            data.slice(0, 8).map((r) => (
              <Link
                key={r.id}
                href={`/devis/${r.id}`}
                className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-muted/50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className={cn(
                      "h-2 w-2 shrink-0 rounded-full",
                      r.jours > 7
                        ? "bg-red-500"
                        : r.jours > 3
                          ? "bg-amber-500"
                          : "bg-yellow-400"
                    )}
                  />
                  <span className="truncate max-w-[120px]">{r.client}</span>
                </div>
                <span className="shrink-0 font-mono text-xs text-muted-foreground">
                  {r.amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                </span>
              </Link>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Prospects en cours ──
export function ProspectsEnCoursList({ data }: { data: ProspectItem[] }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-muted-foreground">
            Prospects en cours
          </h3>
          {data.length > 0 && (
            <span className="text-xs font-mono text-muted-foreground">
              {data.length}
            </span>
          )}
        </div>
        <div className="max-h-[180px] space-y-1.5 overflow-y-auto">
          {data.length === 0 ? (
            <p className="py-8 text-center text-xs text-muted-foreground">
              Aucun prospect
            </p>
          ) : (
            data.slice(0, 8).map((p) => (
              <Link
                key={p.id}
                href={`/tiers/${p.id}`}
                className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-muted/50"
              >
                <span className="truncate max-w-[150px]">{p.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {p.nbDevis} devis
                </span>
              </Link>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
